import React, { useState, useEffect, useCallback } from 'react';
import { User, Building2, Key, Edit2, X, Save, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import UserService from '../../services/userService';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ProfileCard,
  ProfileBadge,
  ProfileListItem,
  profileInputBase,
  profileInputDisabled,
  profileLabelClass,
} from '../../components/profile/ProfileUi';

function formatIban(val) {
  if (!val) return '';
  return val.replace(/\s+/g, '').replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 26).replace(/(.{4})/g, '$1 ').trim();
}
const sanitizeIban = (val) => (val ? val.replace(/[^0-9A-Za-z]/g, '').toUpperCase().slice(0, 26) : '');

const Profile = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profileData, setProfileData] = useState({
    name: '', surname: '', email: '', phoneNumber: '', companyName: '', taxNumber: '',
    companyType: '', businessArea: '', city: '', address: '', title: '', ibanNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: '' });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '', surname: user.surname || '', email: user.email || '',
        phoneNumber: user.phoneNumber || '', companyName: user.companyName || '',
        taxNumber: user.taxNumber || '', companyType: user.companyType || '',
        businessArea: user.businessArea || '', city: user.city || '',
        address: user.address || '', title: user.title || '',
        ibanNumber: user.ibanNumber ? formatIban(user.ibanNumber) : '',
      });
    }
  }, [user]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const result = await UserService.getCurrentUserProfile();
      if (result.success && result.data) {
        const d = result.data.data || result.data;
        setProfileData({
          name: d.name || '', surname: d.surname || '', email: d.email || '',
          phoneNumber: d.phoneNumber || '', companyName: d.companyName || '',
          taxNumber: d.taxNumber || '', companyType: d.companyType || '',
          businessArea: d.businessArea || '', city: d.city || '',
          address: d.address || '', title: d.title || '',
          ibanNumber: d.ibanNumber ? formatIban(d.ibanNumber) : d.iban ? formatIban(d.iban) : '',
        });
      } else {
        setError(result.message || 'Profile could not be retrieved');
      }
    } catch {
      setError(t('profile.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleChange = (field, value) => {
    setProfileData((p) => ({ ...p, [field]: field === 'ibanNumber' ? formatIban(value) : value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    const iban = sanitizeIban(profileData.ibanNumber);
    if (iban && iban.length > 32) { setError('IBAN cannot exceed 32 characters.'); setSaving(false); return; }
    try {
      const result = await UserService.updateProfile({
        name: profileData.name, surname: profileData.surname, phoneNumber: profileData.phoneNumber,
        title: profileData.title, address: profileData.address, ibanNumber: iban || null,
      });
      if (result.success) {
        setSuccess(result.message || t('profile.saveSuccess'));
        toast.success(result.message || t('profile.saveSuccess'));
        setEditMode(false);
        const stored = JSON.parse(localStorage.getItem('userData') || '{}');
        localStorage.setItem('userData', JSON.stringify({
          ...stored, name: profileData.name, surname: profileData.surname,
          phoneNumber: profileData.phoneNumber, title: profileData.title,
          address: profileData.address, ibanNumber: iban || '',
        }));
        window.dispatchEvent(new CustomEvent('userLogin'));
      } else {
        setError(result.message || t('profile.saveError'));
        toast.error(result.message || t('profile.saveError'));
      }
    } catch {
      setError(t('profile.saveError'));
      toast.error(t('profile.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = () => {
    if (confirmModal.action === 'save') saveProfile();
    else if (confirmModal.action === 'cancel') {
      setEditMode(false);
      if (user) {
        setProfileData({
          name: user.name || '', surname: user.surname || '', email: user.email || '',
          phoneNumber: user.phoneNumber || '', companyName: user.companyName || '',
          taxNumber: user.taxNumber || '', companyType: user.companyType || '',
          businessArea: user.businessArea || '', city: user.city || '',
          address: user.address || '', title: user.title || '',
          ibanNumber: user.ibanNumber ? formatIban(user.ibanNumber) : '',
        });
      }
    }
    setConfirmModal({ open: false, action: '' });
  };

  const fullName = [profileData.name, profileData.surname].filter(Boolean).join(' ') || t('profile.title');
  const userInitials = () => {
    if (profileData.name && profileData.surname) return `${profileData.name[0]}${profileData.surname[0]}`.toUpperCase();
    return profileData.name?.[0]?.toUpperCase() || 'U';
  };

  const Field = ({ label, field, editable = true, type = 'text', mono = false }) => {
    const isEditable = editMode && editable;
    return (
      <div>
        <label className={profileLabelClass}>{label}</label>
        {field === 'address' ? (
          <textarea
            value={profileData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            disabled={!isEditable}
            rows={3}
            className={`w-full px-4 py-3 rounded-xl border text-sm transition-all outline-none resize-none ${
              isEditable
                ? 'bg-gray-50 dark:bg-white/5 focus:ring-2 focus:ring-[#24299d]/20 focus:border-[#24299d] border-gray-200 dark:border-white/10 text-gray-900 dark:text-white'
                : profileInputDisabled
            }`}
          />
        ) : (
          <input
            type={type}
            value={profileData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            disabled={!isEditable}
            className={`${isEditable ? profileInputBase : profileInputDisabled} ${mono ? 'font-mono tracking-wider' : ''}`}
          />
        )}
        {field === 'email' && !editable && <p className="mt-1 text-xs text-gray-400">{t('profile.emailNote')}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-10 h-10 border-4 border-[#24299d]/20 border-t-[#24299d] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto">
      {/* Header Card — tahsis style */}
      <div className="bg-white dark:bg-[#1C1C1E] rounded-[28px] p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 shadow-sm border border-gray-100 dark:border-white/10">
        <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-[#24299d] to-[#3840c4] flex items-center justify-center text-white text-3xl font-bold shadow-lg shrink-0">
          {userInitials()}
        </div>
        <div className="flex-1 text-center md:text-left min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white truncate">{fullName}</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5 truncate">{profileData.email}</p>
          <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
            {user?.role && <ProfileBadge type="blue">{user.role}</ProfileBadge>}
            {profileData.title && <ProfileBadge type="success">{profileData.title}</ProfileBadge>}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/change-password')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            <Key size={15} />
            {t('profile.changePassword')}
          </button>
          <button
            type="button"
            onClick={() => (editMode ? setConfirmModal({ open: true, action: 'cancel' }) : setEditMode(true))}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              editMode
                ? 'border border-red-200 dark:border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'bg-[#24299d] hover:bg-[#1b2080] text-white shadow-sm'
            }`}
          >
            {editMode ? <><X size={15} /> {t('profile.cancelEdit')}</> : <><Edit2 size={15} /> {t('profile.editProfile')}</>}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Info */}
        <ProfileCard
          title={t('profile.personalInfo')}
          icon={User}
          color="bg-blue-100 dark:bg-blue-900/30"
        >
          {editMode ? (
            <div className="grid grid-cols-1 gap-4">
              <Field label={t('profile.firstName')} field="name" />
              <Field label={t('profile.lastName')} field="surname" />
              <Field label={t('profile.email')} field="email" editable={false} type="email" />
              <Field label={t('profile.phone')} field="phoneNumber" />
              <Field label={t('profile.titleLabel')} field="title" />
              <Field label={t('profile.address')} field="address" />
              <div>
                <label className={profileLabelClass}>{t('profile.iban')}</label>
                <input
                  value={profileData.ibanNumber}
                  onChange={(e) => handleChange('ibanNumber', e.target.value)}
                  placeholder="TR44 4444 4444 4444 4444 4444 44"
                  className={`${profileInputBase} font-mono tracking-wider`}
                />
                <p className="mt-1 text-xs text-gray-400">{t('profile.ibanHint')}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <ProfileListItem label={t('profile.firstName')} value={profileData.name} />
              <ProfileListItem label={t('profile.lastName')} value={profileData.surname} />
              <ProfileListItem label={t('profile.email')} value={profileData.email} subtext={t('profile.emailNote')} />
              <ProfileListItem label={t('profile.phone')} value={profileData.phoneNumber} />
              <ProfileListItem label={t('profile.titleLabel')} value={profileData.title} />
              <ProfileListItem label={t('profile.address')} value={profileData.address} />
              <ProfileListItem label={t('profile.iban')} value={profileData.ibanNumber} />
            </div>
          )}
        </ProfileCard>

        {/* Company Info */}
        <ProfileCard
          title={t('profile.companyInfo')}
          icon={Building2}
          color="bg-green-100 dark:bg-green-900/30"
        >
          {editMode ? (
            <div className="grid grid-cols-1 gap-4">
              <Field label={t('profile.companyName')} field="companyName" editable={false} />
              <Field label={t('profile.taxNumber')} field="taxNumber" editable={false} />
              <Field label={t('profile.companyType')} field="companyType" editable={false} />
              <Field label={t('profile.businessArea')} field="businessArea" editable={false} />
              <Field label={t('profile.city')} field="city" editable={false} />
            </div>
          ) : (
            <div className="space-y-1">
              <ProfileListItem label={t('profile.companyName')} value={profileData.companyName} />
              <ProfileListItem label={t('profile.taxNumber')} value={profileData.taxNumber} />
              <ProfileListItem label={t('profile.companyType')} value={profileData.companyType} />
              <ProfileListItem label={t('profile.businessArea')} value={profileData.businessArea} />
              <ProfileListItem label={t('profile.city')} value={profileData.city} />
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{t('profile.companyNote')}</p>
        </ProfileCard>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
          <Check size={15} /> {success}
        </div>
      )}

      {editMode && (
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setConfirmModal({ open: true, action: 'cancel' })}
            disabled={saving}
            className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            {t('profile.cancelEdit')}
          </button>
          <button
            type="button"
            onClick={() => setConfirmModal({ open: true, action: 'save' })}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#24299d] hover:bg-[#1b2080] text-white text-sm font-semibold shadow-sm transition-all active:scale-95 disabled:opacity-60"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('profile.saving')}
              </>
            ) : (
              <>
                <Save size={15} />
                {t('profile.saveChanges')}
              </>
            )}
          </button>
        </div>
      )}

      {confirmModal.open && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setConfirmModal({ open: false, action: '' })} aria-hidden />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-[#1C1C1E] rounded-[28px] shadow-2xl border border-gray-100 dark:border-white/10 w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {confirmModal.action === 'save' ? t('profile.confirmSaveTitle') : t('profile.confirmCancelTitle')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {confirmModal.action === 'save' ? t('profile.confirmSaveMessage') : t('profile.confirmCancelMessage')}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmModal({ open: false, action: '' })}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5"
                >
                  {t('common.no')}
                </button>
                <button
                  type="button"
                  onClick={confirmAction}
                  className={`px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-all ${
                    confirmModal.action === 'save' ? 'bg-[#24299d] hover:bg-[#1b2080]' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {confirmModal.action === 'save' ? t('profile.saveConfirm') : t('profile.cancelConfirm')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;
